/**
 * Time, for a film: where in a span a moment falls, and how motion eases through it.
 *
 * Every film is a pure function of time — no state carried from one frame to the next —
 * so these are all there is to motion: a span's progress, eased, mixed between two
 * values.
 */

export function clamp(value: number, low = 0, high = 1): number {
  return Math.min(high, Math.max(low, value));
}

/** How far `t` is through `[start, end]`, from 0 before it to 1 after it. */
export function span(t: number, start: number, end: number): number {
  return end <= start ? (t >= end ? 1 : 0) : clamp((t - start) / (end - start));
}

export function mix(a: number, b: number, k: number): number {
  return a + (b - a) * k;
}

/** Where in its span a drop eased by `ease.bounce` first touches down. */
export const BOUNCE_LANDS = 0.72;

export const ease = {
  inOut: (k: number): number => (k < 0.5 ? 2 * k * k : 1 - (-2 * k + 2) ** 2 / 2),
  out: (k: number): number => 1 - (1 - k) ** 3,
  in: (k: number): number => k * k * k,
  /** Past the mark and back: a thing that lands with some spring in it. */
  back: (k: number): number => {
    const c = 1.9;
    return 1 + (c + 1) * (k - 1) ** 3 + c * (k - 1) ** 2;
  },
  /** A drop that bounces once on the floor. */
  bounce: (k: number): number => {
    if (k < BOUNCE_LANDS) {
      return (k / BOUNCE_LANDS) ** 2;
    }
    const b = (k - 0.86) / 0.14;
    return 1 - 0.09 * (1 - b * b);
  },
};

/** 0 → 1 → 0 across a span: a flash, a squash, a beat. */
export function pulse(t: number, start: number, end: number): number {
  const k = span(t, start, end);
  return Math.sin(k * Math.PI);
}

/** Which of a list of cues is current at `t`, as an index; -1 before the first. */
export function cueAt(t: number, starts: readonly number[]): number {
  let found = -1;
  starts.forEach((start, index) => {
    if (t >= start) {
      found = index;
    }
  });
  return found;
}

/** A point a moving thing passes through, and when. */
export interface Key {
  at: number;
  x: number;
  y: number;
}

/** Where a thing moving through `keys` is at `u`: eased from each key to the next. */
export function along(keys: readonly Key[], u: number): { x: number; y: number } {
  if (u <= keys[0]!.at) {
    return keys[0]!;
  }
  for (let i = 1; i < keys.length; i += 1) {
    const a = keys[i - 1]!;
    const b = keys[i]!;
    if (u <= b.at) {
      const k = ease.inOut(span(u, a.at, b.at));
      return { x: mix(a.x, b.x, k), y: mix(a.y, b.y, k) };
    }
  }
  return keys[keys.length - 1]!;
}
